import CartDrawer from '../globals/cart';
import Drawer from '../globals/drawer';
import initTransparentHeader from '../globals/init-transparent-header';
import setMainSpacing from '../globals/set-main-spacing';
import {hoverDisclosure} from '../features/header-hover-disclosure';
import {navSearch} from '../features/predictive-search';
import {register} from '../vendor/theme-scripts/theme-sections';

const selectors = {
  header: '[data-site-header]',
  announcementBar: '[data-announcement-wrapper]',
  collectionFilters: '[data-collection-filters]',
  logo: '[data-logo]',
  mobileNavDropdownTrigger: '[data-collapsible-trigger]',
  nav: '[data-nav]',
  navIcons: '[data-nav-icons]',
  navItem: '[data-nav-item]',
  navLinkMobile: '[data-nav-link-mobile]',
  wrapper: '[data-wrapper]',
  headerBackground: '[data-header-background]',
  cartPage: '[data-cart-page]',
};

const classes = {
  jsDrawerOpenAll: ['js-drawer-open', 'js-drawer-open-cart', 'js-quick-view-visible'],
  headerTransparent: 'site-header--transparent',
  headerLoading: 'site-header--loading',
  headerHovered: 'site-header--hovered',
  headerMenuOpened: 'site-header--menu-opened',
  hasScrolled: 'has-scrolled',
  hideHeader: 'hide-header',
  navCompress: 'nav--compress',
  headerSticky: 'header--sticky',
  isVisible: 'is-visible',
  noOutline: 'no-outline',
};

const sections = {};

class Header {
  constructor(container) {
    this.container = container;
    this.background = document.querySelector(selectors.headerBackground);
    this.header = container;
    this.headerSection = container.parentNode;
    this.headerWrapper = container.querySelector(selectors.wrapper);
    this.logo = container.querySelector(selectors.logo);
    this.nav = container.querySelector(selectors.nav);
    this.navIcons = container.querySelector(selectors.navIcons);
    this.headerStateEvent = (event) => this.headerState(event);
    this.handleTouchstartEvent = (event) => this.handleTouchstart(event);
    this.updateBackgroundHeightEvent = (event) => this.updateBackgroundHeight(event);
    this.hasScrolled = false;
    this.hasCollectionFilters = document.querySelector(selectors.collectionFilters);

    initTransparentHeader();
    setMainSpacing();

    this.controlNav();
    this.initMobileNav();

    window.addEventListener('load', this.controlNav.bind(this));
    document.addEventListener('theme:resize:width', this.controlNav.bind(this));

    if (!this.hasCollectionFilters) {
      this.initStickyHeader();
    } else {
      this.header.classList.remove(classes.headerLoading);
    }

    this.handleBackgroundEvents();

    if (!document.querySelector(selectors.cartPage)) {
      window.cart = new CartDrawer();
    }
    window.drawer = new Drawer();

    document.body.addEventListener('touchstart', this.handleTouchstartEvent, {passive: true});
  }

  handleTouchstart(event) {
    const isInHeader = this.header.contains(event.target);
    const activeNavItem = this.header.querySelector(`.${classes.isVisible}${selectors.navItem}`);

    if (!isInHeader && activeNavItem) {
      activeNavItem.dispatchEvent(new Event('mouseleave', {bubbles: true}));
    }
  }

  initStickyHeader() {
    this.position = this.header.dataset.position;

    if (this.position === 'fixed') {
      this.headerSection.classList.add(classes.headerSticky);
    } else {
      this.headerSection.classList.remove(classes.headerSticky);
    }

    this.header.classList.remove(classes.headerLoading);
    this.headerState();
    document.addEventListener('theme:scroll', this.headerStateEvent);
  }

  // Switch to "compact" header on scroll
  headerState(event) {
    const headerHeight = parseInt(this.header.dataset.height || this.header.offsetHeight);
    const announcementBar = document.querySelector(selectors.announcementBar);
    const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
    const pageOffset = headerHeight + announcementHeight;
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollUp = event && event.detail && event.detail.up;
    let tempHeight = headerHeight;

    if (this.position === 'fixed') {
      // Show compact header when scroll down
      if (currentScrollTop > pageOffset) {
        document.body.classList.add(classes.hasScrolled);
        this.hasScrolled = true;
        this.header.classList.remove(classes.headerTransparent);
        tempHeight = window.hasScrolledHeaderHeight;
      } else {
        document.body.classList.remove(classes.hasScrolled);
        this.hasScrolled = false;
        tempHeight = headerHeight;

        if (window.isHeaderTransparent) {
          this.header.classList.add(classes.headerTransparent);
        }
      }

      if (document.documentElement.style.getPropertyValue('--header-height') !== `${tempHeight}px`) {
        document.documentElement.style.setProperty('--header-height', `${tempHeight}px`);
      }

      // Hide compact header when scroll back to top
      if (currentScrollTop < pageOffset && scrollUp) {
        document.body.classList.add(classes.hideHeader);
      } else {
        document.body.classList.remove(classes.hideHeader);
      }

      // Update header background height if users scroll the page with their mouse over the header or over an opened nav menu
      if (this.header.classList.contains(classes.headerHovered)) {
        this.background.style.setProperty('--header-background-height', `${tempHeight}px`);

        const activeNavItem = this.header.querySelector(`.${classes.isVisible}${selectors.navItem}`);

        if (activeNavItem) {
          activeNavItem.dispatchEvent(new Event('mouseenter', {bubbles: true}));
        }
      }
    }
  }

  handleBackgroundEvents() {
    this.headerWrapper.addEventListener('mouseenter', this.updateBackgroundHeightEvent);

    this.headerWrapper.addEventListener('mouseleave', this.updateBackgroundHeightEvent);

    this.header.addEventListener('focusout', this.updateBackgroundHeightEvent);

    document.addEventListener('theme:cart:close', this.updateBackgroundHeightEvent);

    // Helps fixing Safari issues with background not being updated on search close and mouse over the header
    document.addEventListener('theme:search:close', this.updateBackgroundHeightEvent);
  }

  updateBackgroundHeight(event) {
    const isDesktop = window.innerWidth >= window.theme.sizes.large;
    const isFocusEnabled = !document.body.classList.contains(classes.noOutline);
    const isNotTabbingOnDesktop = isDesktop && !isFocusEnabled;

    if (!event) return;

    // Update header background height on:
    // 'mouseenter' event
    if (event.type === 'mouseenter') {
      let popupsVisible = false;

      classes.jsDrawerOpenAll.forEach((popupClass) => {
        if (document.body.classList.contains(popupClass)) {
          popupsVisible = true;
        }
      });

      // Prevent background update:
      // On visible popups
      if (popupsVisible) return;

      // Update header background height:
      this.headerHeight = this.hasScrolled ? window.hasScrolledHeaderHeight : this.header.offsetHeight;

      this.header.classList.add(classes.headerHovered);

      if (!this.header.classList.contains(classes.headerMenuOpened)) {
        this.background.style.setProperty('--header-background-height', `${this.headerHeight}px`);
      }

      return;
    }

    // Remove header background and handle focus on:
    // 'mouseleave' event
    // 'theme:cart:close' event
    // 'theme:search:close' event
    // 'focusout' event

    if (event.type === 'focusout' && !isDesktop) return;
    if (event.type === 'theme:search:close' && !isNotTabbingOnDesktop) return;

    requestAnimationFrame(() => {
      const focusOutOfHeader = document.activeElement.closest(selectors.header) === null;

      if (event.type === 'focusout') {
        if (!focusOutOfHeader) return;
      }

      this.header.classList.remove(classes.headerHovered);
      this.background.style.setProperty('--header-background-height', '0px');

      if (!isFocusEnabled) {
        document.activeElement.blur();
      }
    });
  }

  controlNav() {
    // Reset nav to normal state
    this.nav.classList.remove(classes.navCompress);

    // Subtract 50 from width to give space between the logo and links
    const gap = 20; // Gap between Logo and Nav links
    const isNavCentered = this.header.dataset.navAlignment === 'center';
    const isNavLeft = this.header.dataset.navAlignment === 'left';
    const headerWrapperStyles = this.headerWrapper.currentStyle || window.getComputedStyle(this.headerWrapper);
    const headerWidth = this.headerWrapper.clientWidth - parseFloat(headerWrapperStyles.paddingLeft) - parseFloat(headerWrapperStyles.paddingRight);
    const logoWidth = this.logo ? this.logo.offsetWidth : 0;
    const navIconsWidth = this.navIcons ? this.navIcons.offsetWidth : 0;
    let maxNavWidth = headerWidth - logoWidth - navIconsWidth - gap;
    let navItemsWidth = this.getNavItemsWidth();

    if (isNavCentered) {
      maxNavWidth = headerWidth - (Math.max(logoWidth, navIconsWidth) + gap) * 2;
    }

    if (isNavLeft) {
      maxNavWidth = (headerWidth - (logoWidth + gap * 2)) / 2;
    }

    if (navItemsWidth > maxNavWidth) {
      this.nav.classList.add(classes.navCompress);
    } else {
      this.nav.classList.remove(classes.navCompress);
    }
  }

  getNavItemsWidth() {
    // Reset nav items width
    let navItemsWidth = 0;
    const navItems = this.nav.querySelectorAll(selectors.navItem);

    if (navItems.length) {
      navItems.forEach((item) => {
        // Round up to be safe
        navItemsWidth += Math.ceil(item.offsetWidth);
      });
    }

    return navItemsWidth;
  }

  initMobileNav() {
    if (theme.settings.mobileMenuBehaviour === 'link') {
      return;
    }

    const navMobileLinks = this.headerSection.querySelectorAll(selectors.navLinkMobile);
    if (navMobileLinks.length) {
      navMobileLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
          const hasDropdown = link.parentNode.querySelectorAll(selectors.mobileNavDropdownTrigger).length;
          const dropdownTrigger = link.nextElementSibling;

          if (hasDropdown) {
            e.preventDefault();
            dropdownTrigger.dispatchEvent(new Event('click'), {bubbles: true});
          }
        });
      });
    }
  }

  onUnload() {
    document.body.classList.remove(...classes.jsDrawerOpenAll);
    document.removeEventListener('theme:scroll', this.headerStateEvent);
    document.removeEventListener('theme:resize:width', this.controlNav);
    document.removeEventListener('theme:cart:close', this.updateBackgroundHeightEvent);
    document.removeEventListener('theme:search:close', this.updateBackgroundHeightEvent);
    document.body.removeEventListener('touchstart', this.handleTouchstartEvent);
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));

    if (typeof window.cart.unload === 'function') {
      window.cart.unload();
    }

    if (typeof window.drawer.unload === 'function') {
      window.drawer.unload();
    }
  }
}

const headerSection = {
  onLoad() {
    sections[this.id] = new Header(this.container);
  },
  onUnload() {
    sections[this.id].onUnload();
  },
};

register('header', [headerSection, hoverDisclosure, navSearch]);
